import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { ref, getBlob } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage, auth } from '../firebase/config';
import { Factory, RefreshCw, Download, AlertTriangle, Gauge, X } from 'lucide-react';
import { mergeJigRows, monthlyJigUsage } from '../utils/jigUsage';
import { getFeatures, isModelAllowedLab } from '../utils/features';   // 3D 모델 허용 기공소 목록 (2026-10-30)
import JigUsageBar, { useJigUsage } from './common/JigUsageBar';   // 🧩 플랜 한도 대비 % 막대 (2026-10-30 구독 플랜제)
import JigModelSettings from './JigModelSettings';   // ⚙️ 지그·3D 모델 설정 카드 (2026-11-02 대표 "설정은 웹에서")
import { pcDropsModel, dropModelText } from '../utils/agentModelSupport';   // 옛 덴클 프로그램이 모델을 빼는지 (2026-11-02)
import JigPcCases from './JigPcCases';   // 🖥️ PC에서 찾은 케이스 — 웹 주도 지그 구조 (2026-10-30, 계획_지그_웹주도구조 §3)
import PageHeader from './common/PageHeader';
import { PhotoImg, safeName, zipToStlList } from './common/jigFiles';
import { useJigConsent, JigConsentGate } from './common/JigConsentGate';   // 🔐 지그 첫 이용 동의(공용) — 동의 전 jigResults(환자정보) 비노출

// 완성 지그 3D 미리보기(STL 뷰어) — three.js 는 열 때만 로드(메인 번들 제외)
// 결과 사진 (2026-10-24 대표 "3D 미리보기는 없애고 사진으로") — 작업자가 찍은 PNG(jig-results/model-results, 7일 보관).
//   지그만 → 지그 사진, 모델만 → 모델 사진, 둘 다 → 결합 사진을 대표로. URL 은 한 번 받아 두고 재사용.
// PhotoImg·safeName·zipToStlList → common/jigFiles.js (외주 의뢰 페이지와 공용, 2026-10-30)

// 🧩 지그 (통합) — '지그 생성'(/jig-local) + '지그 결과'(/jig) 를 하나로 합친 상태 테이블 (2026-10-21)
// ⚠️ 과금/결제 로직 없음 — 사용량은 "표시"만. KISPG·청구 연동 일절 없음.
// ⚠️ 환자정보 경계: 환자·치과·치식은 **jigResults(본인 labId) 문서에 이미 담긴 범위만** 보여준다(기존 '지그 결과' 화면과 동일 범위).
//    새로 웹에 올리지 않고, 로그도 남기지 않는다. jigJobs(대기·생성중·실패)에는 환자정보가 없으므로 그 행은 '—' 로 둔다.
// 노출 = (isJigLocalOn || isJigWebOn) && 기공소 — App.js 라우트·MainLayout 메뉴에서 게이팅(기존 플래그 범위).

// ── 포맷 유틸 ──
function toDate(ts) {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts instanceof Date) return ts;
    if (typeof ts === 'number') return new Date(ts);
    return null;
}
function fmtDate(ts) {
    const d = toDate(ts);
    if (!d) return '—';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fmtDateTime(ts) {
    const d = toDate(ts);
    if (!d) return '—';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
function daysLeft(expiresAt) {
    const d = toDate(expiresAt);
    if (!d) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}
// 실패 사유코드 → 짧은 한글 (대표 2026-10-24 "실패 원인을 간단하게" — 코드 집합은 서버 jig-queue/model-queue WORKER_FAIL_CODES·purgeDecision 과 같다)
const ERR_MAP = {
    claim_exhausted: '재시도 초과',
    stale_7d: '7일 미처리',
    'sign-failed': '서버 서명 오류',
    engine_no_arch: '합본 없음',
    engine_plaster: '석고 스캔 (모델 안 만듦)',
    engine_tooth_info: '치식 정보 불일치',
    engine_no_jig: '커스텀 아님',
    abutment_not_closed: '어버트먼트 안 닫힘',
    engine_timeout: '시간 초과',
    engine_error: '엔진 오류',
};
// 실패 사유 → 뭘 하면 되는지 한 줄 (툴팁·보조 설명). 서버 FAIL_REASON_LABEL 과 같은 뜻, 더 짧게.
const ERR_HINT = {
    claim_exhausted: '작업자 PC 연결이 여러 번 끊겼습니다. 다시 시도하세요.',
    stale_7d: '7일 안에 처리되지 않아 경유본이 삭제됐습니다. 프로그램에서 다시 보내세요.',
    engine_no_arch: 'exocad에서 장면을 STL로 내보내(악궁·커스텀 켜고) 케이스 폴더에 넣은 뒤 다시 보내세요.',
    engine_plaster: '석고 스캔 케이스는 모델을 만들지 않습니다. 구강스캔 케이스만 보내세요.',
    engine_tooth_info: '케이스의 치식 정보를 확인해 주세요.',
    engine_no_jig: '*-abutment_cad.stl 이 있는 커스텀 케이스만 지그·모델이 됩니다.',
    abutment_not_closed: 'CAD에서 어버트먼트를 다시 내보내 주세요.',
    engine_timeout: '파일이 너무 크거나 작업자 PC가 느렸습니다. 다시 시도하세요.',
    engine_error: '다시 시도하세요. 반복되면 덴클에 알려 주세요.',
};
function errText(code) {
    if (!code) return '실패';
    if (ERR_MAP[code]) return ERR_MAP[code];
    const s = String(code);
    if (s.startsWith('engine_fail')) return ('엔진 오류' + s.slice(11).replace(/^:\s*/, ' — ')).slice(0, 40);
    if (s.startsWith('engine')) return '엔진 오류';
    return `실패 (${s.slice(0, 40)})`;
}
function errHint(code) { return ERR_HINT[code] || ''; }

// 상태(구분) 표시 — 판정(deriveJigStatus)은 utils/jigUsage
const STATUS_META = {
    pending: { label: '대기', color: '#64748b', bg: '#f1f5f9' },
    claimed: { label: '생성 중', color: '#2563eb', bg: '#eff6ff' },
    done: { label: '완성', color: '#0f766e', bg: '#f0fdfa' },
    error: { label: '실패', color: '#dc2626', bg: '#fef2f2' },
    model: { label: '모델만', color: '#7c3aed', bg: '#f5f3ff' },   // 지그 없이 3D 모델만 주문한 케이스 (대표 2026-10-24)
};

function Jig({ user }) {
    const labId = user?.companyId || auth.currentUser?.uid;
    const consent = useJigConsent();   // 🔐 지그 첫 이용 동의 — 동의 전에는 jigResults(환자정보·다운로드)를 열지 않는다

    const [jobs, setJobs] = useState([]);       // jigJobs (상태)
    const [results, setResults] = useState([]); // jigResults (환자정보·다운로드)
    const [dlMap, setDlMap] = useState({});     // jobId → 최근 다운로드일(✓받음)
    const [deviceNames, setDeviceNames] = useState({}); // deviceId → 'PC 관리' 이름표
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [busy, setBusy] = useState(false);
    const [photoRow, setPhotoRow] = useState(null);   // 결과 사진 크게 보기가 열린 행
    const [guide, setGuide] = useState(null);   // 설정 방법 모달: 'exocad' | '3shape' | null (2026-10-27)
    const [photoTab, setPhotoTab] = useState(0);
    const [search, setSearch] = useState('');          // 환자·치과·치식·지그번호 검색
    // 3D 모델 (2026-10-23, 서버 model-queue.js) — 지그와 같은 케이스 jobId 로 따로 만든다. 상태·보관본만 jobId 로 붙여 보여 준다.
    const [modelJobs, setModelJobs] = useState({});     // jobId → modelJobs 문서
    const [modelResults, setModelResults] = useState({}); // jobId → modelResults 문서(7일 보관)
    // 3D 모델 허용 여부 (2026-10-30 목록 / 2026-11-02 config/features.modelOpenAll 이면 전체). 로딩 전·실패·목록에 없음 = false(모델 버튼·선택지 숨김, '준비 중' 배지)
    const [modelOpen, setModelOpen] = useState(false);
    const [features, setFeatures] = useState(null);       // 옛 PC 「모델 빠짐」 판정용(11-02)
    const [pcVersions, setPcVersions] = useState({});     // deviceId → 덴클 프로그램 버전(PC 목록 cases_* 문서)
    const onDevices = useCallback((list) => {
        const m = {};
        (list || []).forEach((d) => { if (d.deviceId) m[d.deviceId] = d.version || ''; });
        setPcVersions(m);
    }, []);
    // 이 줄을 보낸 PC 가 옛 버전이라 모델을 빼는지 — 버전을 모르면 false
    const rowDropsModel = (row) => modelOpen && !!row?.meta?.deviceId && pcDropsModel(pcVersions[row.meta.deviceId], features, labId);
    useEffect(() => {
        let alive = true;
        getFeatures()
            .then((f) => { if (alive) { setModelOpen(isModelAllowedLab(f, labId)); setFeatures(f || null); } })
            .catch((e) => { console.error('[지그] 기능 설정 조회 실패 — 3D 모델 숨김 유지:', e?.message || e); });
        return () => { alive = false; };
    }, [labId]);
    // 🧩 이번 달 사용량(한도 대비 %) — 서버 getJigUsage. 동의 뒤에만 조회(종전 fail-closed 유지). 실패하면 아래 로컬 개수(usage)로 대신 보인다
    const jigUsage = useJigUsage(consent.agreed === true && !!labId, [labId]);

    // 사진 목록: 지그 2장(옆·위) → 모델 2장(옆·반대쪽) (대표 2026-10-28 "결합 사진은 빼고 지그·모델 두 장씩"). 대표 사진은 첫 번째.
    //   comboPhotoPath 는 이제 같은 결과의 2장째(작업자가 그 자리에 올린다). 옛 결과(결합 사진)도 같은 자리라 그대로 2장째로 보인다.
    const photoList = useCallback((row) => {
        const mr = modelResults[row.jobId] || {};
        const out = [];
        // 양악(치식에 1x·2x 와 3x·4x 가 같이 있음)은 작업자가 1장째 상악 지그·2장째 하악 지그를 따로 올린다 (대표 2026-10-29 "상악하악 지그 사진 따로")
        const tt = (Array.isArray(row.teeth) ? row.teeth : String(row.teeth || '').split(/[,\s]+/)).map((t) => String(t).trim()).filter(Boolean);
        const bothJaws = tt.some((t) => /^[12]/.test(t)) && tt.some((t) => /^[34]/.test(t));
        if (row.photoPath) out.push({ label: bothJaws ? '상악 지그' : '지그 옆', path: row.photoPath });
        if (row.comboPhotoPath) out.push({ label: bothJaws ? '하악 지그' : '지그 위', path: row.comboPhotoPath });
        if (mr.photoPath) out.push({ label: '모델 옆', path: mr.photoPath });
        if (mr.comboPhotoPath) out.push({ label: '모델 반대쪽', path: mr.comboPhotoPath });
        return out;
    }, [modelResults]);

    const load = useCallback(async () => {
        if (!labId) { setLoading(false); return; }
        setLoading(true);
        setLoadError('');
        try {
            // 셋 다 where(labId==) 동등쿼리만 — 복합 인덱스 불필요, 정렬은 클라이언트에서.
            // 독립 조회(allSettled): 부가정보(jigAccessLogs) 하나가 실패해도 핵심 목록(jigJobs·jigResults)은 뜨게 한다.
            // 어느 컬렉션이 막혔는지 각각 로그로 남긴다(조용한 실패 금지).
            const [jR, rR, lR] = await Promise.allSettled([
                getDocs(query(collection(db, 'jigJobs'), where('labId', '==', labId))),
                getDocs(query(collection(db, 'jigResults'), where('labId', '==', labId))),
                getDocs(query(collection(db, 'jigAccessLogs'), where('labId', '==', labId))),
            ]);
            if (jR.status === 'fulfilled') setJobs(jR.value.docs.map(d => ({ id: d.id, ...d.data() })));
            else console.error('[지그] jigJobs 로드 실패:', jR.reason);
            if (rR.status === 'fulfilled') setResults(rR.value.docs.map(d => ({ id: d.id, ...d.data() })));
            else console.error('[지그] jigResults 로드 실패:', rR.reason);
            // 3D 모델 — 지그 목록과 별개 조회(실패해도 지그 목록은 그대로)
            Promise.allSettled([
                getDocs(query(collection(db, 'modelJobs'), where('labId', '==', labId))),
                getDocs(query(collection(db, 'modelResults'), where('labId', '==', labId))),
            ]).then(([mj, mr]) => {
                if (mj.status === 'fulfilled') { const m = {}; mj.value.docs.forEach(d => { m[d.id] = d.data(); }); setModelJobs(m); }
                else console.error('[모델] modelJobs 로드 실패:', mj.reason);
                if (mr.status === 'fulfilled') { const m = {}; mr.value.docs.forEach(d => { m[d.id] = d.data(); }); setModelResults(m); }
                else console.error('[모델] modelResults 로드 실패:', mr.reason);
            });
            if (lR.status === 'fulfilled') {
                const m = {};
                lR.value.docs.forEach(d => {
                    const x = d.data();
                    if (x.action !== 'download') return;
                    const t = toDate(x.at);
                    if (!t) return;
                    const k = x.jobId;
                    if (!m[k] || t > m[k]) m[k] = t;
                });
                setDlMap(m);
            } else {
                console.error('[지그] jigAccessLogs 로드 실패(다운로드 기록만 생략):', lR.reason);
            }
            // 핵심 목록(작업·결과)이 둘 다 실패일 때만 전체 오류로 표시한다.
            if (jR.status === 'rejected' && rR.status === 'rejected') {
                setLoadError('목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        } catch (e) {
            console.error('[지그] 목록 로드 실패:', e);
            setLoadError('목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [labId]);

    // 동의 전에는 jigResults(환자정보)를 불러오지 않는다(fail-closed)
    useEffect(() => { if (consent.agreed) load(); }, [load, consent.agreed]);

    // 'PC 관리'에서 바꾼 PC 이름표(agentStatus.deviceNames, 웹 전용) — 보낸 PC 칸에서 기기명보다 우선 (ProductionBoard 와 같은 방식)
    useEffect(() => {
        if (!labId) return undefined;
        let alive = true;
        getDoc(doc(db, 'agentStatus', labId))
            .then((st) => { if (alive) setDeviceNames((st.exists() && st.data().deviceNames) || {}); })
            .catch((e) => { console.error('[지그] PC 이름표 조회 실패(기기명으로 표시):', e?.message || e); });
        return () => { alive = false; };
    }, [labId]);

    // ── 병합: jobId 기준(jigResults 가 완성·다운로드·환자정보를 덮어씀) ──
    const rows = useMemo(() => {
        // 외주(CNC·메탈 프린트) 연결 지그는 외주 의뢰 페이지에서만 본다 — 지그 디자인값을 외주업체가 내는 별도 경로(대표 2026-10-30 "분리")
        //   단, 외주 지그를 [다시 만들기]한 것(remakeOf — 서버가 앞 지그가 완성됐던 경우만 붙임)은 기공소가 다시 만든 것이라 여기서 본다(대표 2026-11-02).
        //   외주업체 제작 건수·기공소 사용량 어디에도 세지 않는다(서버 recordVendorJigUsage 건너뜀, channel 은 cnc_outsource 그대로라 사용량 제외).
        const arr = mergeJigRows(jobs, results).filter((r) => r.channel !== 'cnc_outsource' || r.remakeOf);
        // 모델만 주문한 케이스(jigJobs 없음) — modelJobs·modelResults 로 행을 만든다. 환자·치과·치식은 modelResults(7일 보관)에만 있다.
        const have = new Set(arr.map(r => r.jobId));
        Object.entries(modelJobs).forEach(([k, mj]) => {
            if (have.has(k)) return;
            const mr = modelResults[k] || {};
            // 모델만 행도 실패를 구분 칸에서 바로 보이게(_modelFailed) — 받기 칸의 '모델 실패: 사유' 와 함께 (대표 2026-10-24)
            arr.push({ jobId: k, createdAt: mj.createdAt, meta: mj.meta, channel: mj.channel || mr.channel, patient: mr.patient, clinic: mr.clinic, teeth: mr.teeth, expiresAt: mr.expiresAt, _modelOnly: true, _hasResult: false, _status: 'model', _modelFailed: mj.status === 'error', error: mj.error });
        });
        // 최신순(createdAt desc)
        arr.sort((a, b) => {
            const ta = toDate(a.createdAt)?.getTime() || 0;
            const tb = toDate(b.createdAt)?.getTime() || 0;
            return tb - ta;
        });
        return arr;
    }, [jobs, results, modelJobs, modelResults]);

    // ── 검색: 환자·치과·치식·지그번호 (대표 2026-10-22) ──
    const visibleRows = useMemo(() => {
        const kw = search.trim().toLowerCase();
        if (!kw) return rows;
        return rows.filter((r) => {
            const hay = [r.patient, r.clinic, Array.isArray(r.teeth) ? r.teeth.join(' ') : r.teeth, r.jigNo ? `지그 ${r.jigNo}` : '']
                .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(kw);
        });
    }, [rows, search]);

    // ── PC 목록 줄 ↔ 결과 줄 연결(jobId) — PC 카드에 '결과 ↓' 표시용 (2026-10-30)
    const resultJobIds = useMemo(() => new Set(rows.map(r => r.jobId).filter(Boolean)), [rows]);

    // ── 사용량: 이번 달 완성(성공) 개수 — 집계 규칙은 utils/jigUsage(구독 화면과 공용) ──
    const usage = useMemo(() => monthlyJigUsage(rows.filter(r => !r._modelOnly)), [rows]);   // 지그 사용량 — 모델만 행은 빼고 센다

    // ── [다시 만들기] (대표 2026-10-29 "웹에 다시만들기 — 컴퓨터는 켜져 있는데 다른 곳에 있어서") ──
    //   서버는 케이스 zip 을 배달 즉시 지우므로 웹이 직접 만들 수 없다. 요청 표식만 남기고, 케이스 폴더를 가진 덴클 프로그램(켜져 있어야 함)이 받아서 새 작업으로 보낸다.
    const [remakeScope, setRemakeScope] = useState({});
    const [remakeSent, setRemakeSent] = useState({});
    const requestRemake = async (row) => {
        const scope = remakeScope[row.jobId] || (!modelOpen ? 'jig' : (row._modelOnly ? 'model' : 'both'));
        if (!modelOpen && scope !== 'jig') { window.alert('3D 모델 생성은 준비 중입니다. 지그만 다시 만들 수 있습니다.'); return; }   // 모델 미허용 기공소 (2026-10-30)
        // 옛 덴클 프로그램(alpha.121 이하)은 모델을 조용히 뺀다(대표 11-02) — 모델만이면 막고, 지그+모델이면 알린 뒤 지그만
        if (scope !== 'jig' && rowDropsModel(row)) {
            const ver = pcVersions[row.meta.deviceId];
            if (scope === 'model') { window.alert(dropModelText(ver)); return; }
            if (!window.confirm(dropModelText(ver) + '\n\n지그만이라도 다시 만들까요?')) return;
        }
        const label = scope === 'both' ? '지그 + 모델' : (scope === 'jig' ? '지그' : '모델');
        if (!window.confirm(`이 케이스를 다시 만들도록 덴클 프로그램에 요청합니다 — ${label}.\n케이스 폴더가 있는 PC의 덴클 프로그램이 켜져 있어야 하고, 사용량에 다시 셉니다.`)) return;
        try {
            await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'requestJigRemake')({ jobId: row.jobId, scope });
            setRemakeSent(prev => ({ ...prev, [row.jobId]: true }));
        } catch (e) {
            window.alert('다시 만들기 요청 실패: ' + (e?.message || e));
        }
    };
    const remakeControl = (row) => {
        if (remakeSent[row.jobId]) return <span style={styles.muted} title="덴클 프로그램이 받으면 새 작업으로 올라옵니다">{'다시 만들기 요청함'}</span>;
        // 모델 미허용 기공소는 '지그'만 (2026-10-30). 모델만 행은 다시 만들 수 없으므로 표시 안 함
        if (!modelOpen && row._modelOnly) return null;
        const opts = !modelOpen ? [['jig', '지그']] : (row._modelOnly ? [['model', '모델']] : [['both', '지그 + 모델'], ['jig', '지그'], ['model', '모델']]);
        return (
            <span style={styles.actWrap}>
                <select style={styles.remakeSel} value={opts.some(([v]) => v === remakeScope[row.jobId]) ? remakeScope[row.jobId] : opts[0][0]} onChange={(e) => setRemakeScope(prev => ({ ...prev, [row.jobId]: e.target.value }))}>
                    {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
                <button type="button" style={styles.remakeBtn} onClick={() => requestRemake(row)} title="케이스 폴더가 있는 PC의 덴클 프로그램이 받아 새 작업으로 다시 보냅니다">{'다시 만들기'}</button>
            </span>
        );
    };

    // ── 다운로드 (jigResults 완성본만) ──
    const logAccess = async (jobId, action) => {
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'logJigAccess');
            await fn({ jobId, action });
        } catch (e) {
            console.error('[지그] 접속기록 실패(다운로드는 계속):', e?.message || e);
        }
    };
    const downloadOne = async (r) => {
        if (busy) return;
        setBusy(true);
        try {
            const path = r.stlPath || `jig-results/${labId}/${r.jobId}.${r.resultExt || 'zip'}`;
            const blob = await getBlob(ref(storage, path));
            // STL 로 내려받기 — 여러 개면 각각 별도 .stl 파일로 동시에 받는다. (대표 2026-10-22)
            const list = await zipToStlList(blob, r.resultExt || 'zip');
            list.forEach((s) => {
                // 파일 이름에 '지그'를 넣는다(대표 2026-10-28) — 모델 파일(…_모델_지지핀)과 폴더에서 바로 구분되게
                const label = list.length > 1 ? `${safeName(r)}_지그_${s.name.replace(/\.stl$/i, '')}` : `${safeName(r)}_지그`;
                const url = window.URL.createObjectURL(new Blob([s.buffer], { type: 'model/stl' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `${label}.stl`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(url), 10000);
            });
            await logAccess(r.jobId, 'download');
            setDlMap(prev => ({ ...prev, [r.jobId]: new Date() }));
        } catch (e) {
            console.error('[지그] 다운로드 실패:', e);
            alert('내려받기에 실패했습니다. 파일이 만료(삭제)되었을 수 있습니다.');
        } finally {
            setBusy(false);
        }
    };

    // 3D 모델 내려받기 — model-results 보관본(7일). 파일 이름에 지그 행의 이름(치과·환자)을 붙인다.
    const downloadModel = async (row) => {
        if (busy) return;
        const mr = modelResults[row.jobId];
        if (!mr) return;
        setBusy(true);
        try {
            const path = mr.stlPath || `model-results/${labId}/${row.jobId}.${mr.resultExt || 'zip'}`;
            const blob = await getBlob(ref(storage, path));
            const list = await zipToStlList(blob, mr.resultExt || 'zip');
            list.forEach((s) => {
                const url = window.URL.createObjectURL(new Blob([s.buffer], { type: 'model/stl' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `${safeName(row)}_${s.name.replace(/\.stl$/i, '')}.stl`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(url), 10000);
            });
            try { await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'logModelAccess')({ jobId: row.jobId, action: 'download' }); }
            catch (e) { console.error('[모델] 접속기록 실패(다운로드는 계속):', e?.message || e); }
        } catch (e) {
            console.error('[모델] 다운로드 실패:', e);
            alert('3D 모델 내려받기에 실패했습니다. 파일이 만료(삭제)되었을 수 있습니다.');
        } finally {
            setBusy(false);
        }
    };

    // 웹에서는 생성·재생성을 하지 않는다(대표 2026-10-27 "웹에는 지그 다시생성이 없게, 다운받게만") — 실패 건도 덴클 프로그램에서 다시 보낸다.

    return (
        <div>
            <PageHeader
                icon={Factory}
                title={'지그·3D 모델'}
                subtitle={'지그 케이스를 상태별로 보고, 완성된 지그를 내려받습니다.'}
            />

            {/* CAD별 설정 방법 안내 (대표 2026-10-27) — 버튼을 누르면 모달로 설명 */}
            <div style={styles.guideRow}>
                <span style={styles.guideLabel}>{'설정 방법'}</span>
                <button style={styles.guideBtn} onClick={() => setGuide('exocad')}>{'exocad'}</button>
                <button style={styles.guideBtn} onClick={() => setGuide('3shape')}>{'3Shape'}</button>
                {/* 3D 모델은 허용 기공소(config/features.modelAllowedLabIds)만 — 그 밖엔 준비 중 배지 (2026-10-30 대표 결정) */}
                {!modelOpen && <span style={styles.modelSoonBadge}>{'3D 모델 · 준비 중'}</span>}
            </div>
            {/* 지그·3D 모델 설정 (2026-11-02, labSettings/{labId}.jigSettings) — 에이전트 조정값 칸을 웹으로 옮김. 모델 칸은 열린 기공소만 */}
            {labId && <JigModelSettings labId={labId} modelOpen={modelOpen} />}
            {guide && (
                <div style={styles.photoOverlay} onClick={() => setGuide(null)}>
                    <div style={styles.photoModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.photoModalHead}>
                            <span>{guide === 'exocad' ? 'exocad 설정 방법' : '3Shape 설정 방법'}</span>
                            <button style={styles.photoClose} onClick={() => setGuide(null)} aria-label="닫기"><X size={18} /></button>
                        </div>
                        <div style={styles.guideBody}>
                            {(guide === 'exocad' ? GUIDE_EXOCAD : GUIDE_3SHAPE).map((sec, i) => (
                                <div key={i} style={styles.guideSec}>
                                    <div style={styles.guideSecTitle}>{sec.title}</div>
                                    <ol style={styles.guideList}>
                                        {sec.steps.map((t, k) => <li key={k} style={styles.guideItem}>{t}</li>)}
                                    </ol>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            {/* 결과 사진 크게 보기 — 결합/지그/모델 탭 */}
            {photoRow && (() => {
                const list = photoList(photoRow);
                const cur = list[Math.min(photoTab, list.length - 1)];
                return (
                    <div style={styles.photoOverlay} onClick={() => setPhotoRow(null)}>
                        <div style={styles.photoModal} onClick={(e) => e.stopPropagation()}>
                            <div style={styles.photoModalHead}>
                                <span>{(photoRow.jigNo ? `지그 ${photoRow.jigNo} · ` : '') + '결과 사진'}</span>
                                <span style={{ display: 'inline-flex', gap: 6 }}>
                                    {list.map((it, k) => (
                                        <button key={it.label} type="button" onClick={() => setPhotoTab(k)}
                                            style={{ ...styles.rowBtnOutline, ...(k === Math.min(photoTab, list.length - 1) ? { background: '#eef2ff', borderColor: '#6366f1', color: '#3730a3' } : {}) }}>
                                            {it.label}
                                        </button>
                                    ))}
                                </span>
                                <button type="button" style={styles.photoClose} onClick={() => setPhotoRow(null)} title="닫기">
                                    <X size={18} />
                                </button>
                            </div>
                            <div style={styles.photoBig}>
                                {cur ? <PhotoImg path={cur.path} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt={cur.label} /> : '사진이 없습니다.'}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {consent.loading ? (
                <div style={styles.body}><div style={styles.empty}>{'불러오는 중…'}</div></div>
            ) : !consent.agreed ? (
                /* 🔐 첫 이용 동의 게이트(공용) — 동의 기록 전에는 jigResults(환자정보·다운로드)를 열지 않는다(법무 §3) */
                <div style={styles.body}><JigConsentGate consent={consent} padded={false} /></div>
            ) : (
            <div style={styles.body}>
                {/* ── 사용량 (개수만) ── 자동 지그 토글 제거·요금 구간은 구독 화면으로 이동 (2026-10-23 대표) */}
                <div style={styles.topRow}>
                    {/* 🧩 2026-10-30 구독 플랜제: 한도 대비 % 막대(compact). 서버 조회 실패 시에만 종전 로컬 개수 표시 */}
                    <div style={styles.meterCard}>
                        <div style={styles.meterHead}>
                            <Gauge size={16} color="#6366f1" />
                            <span style={styles.meterTitle}>{'이번 달 사용량'}</span>
                            {(!jigUsage.usage || jigUsage.error) && <span style={styles.meterMain}>{`${usage.done}개`}</span>}
                        </div>
                        {jigUsage.usage && !jigUsage.error ? (
                            <JigUsageBar usage={jigUsage.usage} loading={jigUsage.loading} compact />
                        ) : (
                            <div style={styles.meterSub}>
                                <span>{`완성 ${usage.done}개(치식)`}</span>
                                <span style={styles.meterDim}>{`· 총 시도 ${usage.attempts}개 · 실패 ${usage.failed}개`}</span>
                                {jigUsage.error && <span style={{ color: '#dc2626' }}>{`· ${jigUsage.error}`}</span>}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── PC에서 찾은 케이스 (2026-10-30 웹 주도 구조) — 결과 표 위. 에이전트가 올린 목록에서 고르고 명령을 보낸다 */}
                <div style={styles.pcSection}>
                    <div style={styles.pcTitle}>{'PC에서 찾은 케이스'}</div>
                    <div style={styles.pcHint}>{'각 PC 에이전트가 올린 목록입니다. 여기서 고르고 보내면 그 PC가 올립니다.'}</div>
                    <JigPcCases labId={labId} modelOpen={modelOpen} features={features} onDevices={onDevices} deviceNames={deviceNames} resultJobIds={resultJobIds} />
                </div>

                <div style={styles.pcTitle}>{'결과'}</div>
                {/* 검색 + 새로고침 */}
                <div style={styles.toolBar}>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="환자 · 치과 · 치식 · 지그번호 검색"
                        style={styles.searchInput}
                    />
                    <button style={styles.ghostBtn} onClick={load} disabled={loading}>
                        <RefreshCw size={14} />{'새로고침'}
                    </button>
                </div>

                {/* ── 케이스 테이블 ── */}
                {loading ? (
                    <div style={styles.empty}>{'불러오는 중…'}</div>
                ) : loadError ? (
                    <div style={styles.empty}>{loadError}</div>
                ) : visibleRows.length === 0 ? (
                    <div style={styles.empty}>{search.trim() ? '검색 결과가 없습니다.' : '아직 지그 케이스가 없습니다.'}</div>
                ) : (
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    {/* 고정폭: 짧은 칸(구분·지그 번호·생성일)은 좁게, 남는 폭은 환자·치과·치식·보낸 PC 로 (UX 회의 2026-10-23) */}
                                    <th style={{ ...styles.th, width: 124 }}>{'구분'}</th>   {/* 124: '완성'+'외주 재제작' 배지 한 줄(대표 2026-11-05) */}
                                    <th style={styles.th}>{'환자'}</th>
                                    <th style={styles.th}>{'치과'}</th>
                                    <th style={styles.th}>{'치식'}</th>
                                    <th style={{ ...styles.th, width: 84 }}>{'지그 번호'}</th>
                                    <th style={styles.th}>{'보낸 PC'}</th>
                                    <th style={{ ...styles.th, width: 128 }}>{'생성일'}</th>
                                    <th style={{ ...styles.thCenter, width: 120 }}>{'사진'}</th>
                                    <th style={{ ...styles.th, width: 200 }}>{'받기 · 동작'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map(row => {
                                    const st = STATUS_META[row._status];
                                    const teeth = Array.isArray(row.teeth) ? row.teeth.join(', ') : (row.teeth || '');
                                    const isFail = row._status === 'error';
                                    const dl = daysLeft(row.expiresAt);
                                    const soon = dl != null && dl <= 3;
                                    const downloadedAt = dlMap[row.jobId];
                                    // 보낸 PC — 'PC 관리' 이름표 우선, 없으면 에이전트 기기명
                                    const pcLabel = (row.meta?.deviceId && deviceNames[row.meta.deviceId]) || row.meta?.pc || '';
                                    // (모델 받기 버튼은 연결 전까지 숨김 — 비활성 버튼이 고장으로 보인다는 UX 회의 의견, 2026-10-23.
                                    //  결과 묶음에 모델 STL 이 들어오면 여기 추가하고, 지그 받기(downloadOne)는 model_ 파일을 빼야 한다)
                                    return (
                                        <tr key={row.jobId} style={(isFail || row._modelFailed) ? styles.trFail : styles.tr}>
                                            {/* 구분(상태) */}
                                            <td style={styles.td}>
                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap' }}>
                                                <span style={{ ...styles.badge, color: st.color, backgroundColor: st.bg }}>
                                                    {st.label}
                                                </span>
                                                {/* 모델만 행이 실패했으면 '실패' 배지도 함께 — 사유는 받기 칸 (2026-10-24) */}
                                                {row._modelFailed && (
                                                    <span style={{ ...styles.badge, color: STATUS_META.error.color, backgroundColor: STATUS_META.error.bg, marginLeft: 4 }}>{'실패'}</span>
                                                )}
                                                {/* CNC 외주 건 — 제조사 서비스. 이번 달 사용량에서 제외(utils/jigUsage) (2026-10-24) */}
                                                {row.channel === 'cnc_outsource' && (
                                                    row.remakeOf
                                                        ? <span style={{ ...styles.badge, color: '#3730a3', backgroundColor: '#e0e7ff', marginLeft: 4 }} title="외주로 보냈던 케이스를 다시 만든 지그(재제작) — 외주업체·기공소 사용량 어디에도 세지 않습니다">{'외주 재제작'}</span>
                                                        : <span style={{ ...styles.badge, color: '#3730a3', backgroundColor: '#e0e7ff', marginLeft: 4 }} title="CNC 외주 의뢰에 연결된 건 — 기공소 사용량에 세지 않습니다">{'외주'}</span>
                                                )}
                                              </span>
                                            </td>
                                            {/* 환자 */}
                                            <td style={styles.tdEllip} title={row.patient || ''}>{row.patient || '—'}</td>
                                            {/* 치과 */}
                                            <td style={styles.tdEllip} title={row.clinic || ''}>{row.clinic || '—'}</td>
                                            {/* 치식 */}
                                            <td style={styles.tdEllip} title={teeth}>{teeth || '—'}</td>
                                            {/* 지그 번호 (jigNo) — 완성본 식별용. 긴 작업자 ID(claimedBy) 대신 표시 */}
                                            <td style={styles.tdEllip} title={row.jigNo ? `지그 ${row.jigNo}` : ''}>{row.jigNo ? `지그 ${row.jigNo}` : '—'}</td>
                                            {/* 보낸 PC — jigJobs.meta(deviceId·pc, 2026-10-23~). 이전 건·구버전 에이전트는 '—' */}
                                            <td style={styles.tdEllip} title={pcLabel}>{pcLabel || '—'}</td>
                                            {/* 생성일(시:분) — 완성=doneAt(없으면 createdAt), 그 외=createdAt */}
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDateTime((row._status === 'done' && (row.doneAt || row.createdAt)) || row.createdAt)}</td>
                                            {/* 3D 이미지 + 2D 사진 미리보기 자리 (2D = 자리만, 사진 연결은 기능 단계에서 — 2026-10-23 대표) */}
                                            <td style={styles.tdCenter}>
                                                {photoList(row).length ? (
                                                    <button type="button" style={styles.thumbBtn} title="결과 사진 크게 보기" onClick={() => { setPhotoTab(0); setPhotoRow(row); }}>
                                                        <PhotoImg path={photoList(row)[0].path} style={styles.thumbImg} alt="" />
                                                    </button>
                                                ) : '—'}
                                            </td>
                                            {/* 받기 · 동작 */}
                                            <td style={styles.td}>
                                                {row._modelOnly ? null : row._status === 'done' && row._hasResult ? (
                                                    /* '✓ 받음' 표기 없이 늘 같은 [지그 받기] — 누를 때마다 다시 받는다(대표 2026-10-28). 받은 날짜는 툴팁에만 */
                                                    <span style={styles.actWrap}>
                                                        <button style={styles.dlBtn} onClick={() => downloadOne(row)} disabled={busy} title={downloadedAt ? `받음 ${fmtDate(downloadedAt)} · 보관기간(7일) 안이면 다시 받을 수 있습니다` : '지그 STL 받기 — 7일 보관'}>
                                                            <Download size={13} />{'지그 받기'}
                                                        </button>
                                                        {soon && (
                                                            <span style={styles.soonBadge} title="보관 만료 임박">
                                                                <AlertTriangle size={11} />
                                                                {dl <= 0 ? '곧 삭제' : `D-${dl}`}
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : row._status === 'done' ? (
                                                    <span style={styles.muted} title="웹 보관본이 없어 덴클 프로그램에서 받습니다">{'덴클 프로그램에서 받기'}</span>
                                                ) : isFail ? (
                                                    <span style={styles.failWrap}>
                                                        <span style={styles.actWrap}>
                                                            <span style={styles.failText} title={errHint(row.error) || errText(row.error)}>{'지그 실패: ' + errText(row.error)}</span>
                                                            <span style={styles.muted} title="웹에서는 생성하지 않습니다. 덴클 프로그램에서 케이스를 다시 보내 주세요.">{'프로그램에서 다시 보내기'}</span>
                                                        </span>
                                                    </span>
                                                ) : row._status === 'pending' ? (
                                                    <span style={styles.muted} title="지그 생성은 작업자 PC에서 이뤄집니다. 대기 케이스는 순서대로 처리됩니다.">{'생성 대기 중'}</span>
                                                ) : (
                                                    <span style={styles.muted}>{'생성 중…'}</span>
                                                )}
                                                {(row._status === 'done' || isFail || (modelOpen && modelJobs[row.jobId] && ['done', 'delivered', 'error'].includes(modelJobs[row.jobId].status))) && (
                                                    <div style={{ marginTop: 4 }}>{remakeControl(row)}</div>
                                                )}
                                                {/* 모델이 없는데 보낸 PC 가 옛 버전 — 모델이 빠진 이유를 알린다 (대표 11-02 "웹에서도 모델 빠진 거 알 수 있게") */}
                                                {!row._modelOnly && !modelJobs[row.jobId] && rowDropsModel(row) && (
                                                    <div style={styles.dropNote} title={dropModelText(pcVersions[row.meta.deviceId])}>
                                                        {'모델 없음 — 보낸 PC의 덴클 프로그램이 옛 버전이라 모델이 빠졌습니다. 업데이트 후 [다시 만들기]'}
                                                    </div>
                                                )}
                                                {/* 모델 받기·상태는 허용 기공소만 (2026-10-30) */}
                                                {modelOpen && modelJobs[row.jobId] && (
                                                    <div style={{ marginTop: 4 }}>
                                                        {modelResults[row.jobId] ? (
                                                            <button style={styles.dlBtn} onClick={() => downloadModel(row)} disabled={busy} title="3D 모델(위·아래, 교합기 포함) — 7일 보관">
                                                                <Download size={13} />{'모델 받기'}
                                                            </button>
                                                        ) : modelJobs[row.jobId].status === 'error' ? (
                                                            <span style={styles.failWrap}>
                                                                <span style={styles.failText} title={errHint(modelJobs[row.jobId].error) || errText(modelJobs[row.jobId].error)}>{'모델 실패: ' + errText(modelJobs[row.jobId].error)}</span>
                                                            </span>
                                                        ) : modelJobs[row.jobId].status === 'done' || modelJobs[row.jobId].status === 'delivered' ? (
                                                            <span style={styles.muted}>{'모델 완성(웹 보관 꺼짐)'}</span>
                                                        ) : (
                                                            <span style={styles.muted}>{'3D 모델 만드는 중…'}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 보관 안내 */}
                <p style={styles.footNote}>
                    {'완성 지그·3D 모델 결과와 결과 사진은 생성일부터 7일 뒤 자동으로 삭제됩니다. 환자 자료는 우리 기공소 계정에서만 보입니다.'}
                </p>
            </div>
            )}
        </div>
    );
}

// 설정 방법 안내 문안 (대표 2026-10-27). 사실만: 지그생성프로그램 docs/abutment_cases.md 10-26~27 확인 내용 기준.
const GUIDE_EXOCAD = [
    { title: '처음 한 번', steps: [
        '덴클 에이전트에서 [지그] → exocad 케이스 폴더(CAD-Data 등, 완성 케이스가 저장되는 상위 폴더)를 연결합니다.',
        '디자인 완료 시 exocad가 케이스 폴더에 자동 저장하는 파일(*-abutment_cad.stl, .constructionInfo, .dentalProject)은 그대로 둡니다.',
    ] },
    { title: '디자인할 때마다', steps: [
        '커스텀 어버트먼트 디자인을 끝내고 저장합니다.',
        'Expert 모드에서 장면 내보내기(Export scene to mesh / Save scene as STL)로 합본 STL을 케이스 폴더에 저장합니다. 악궁과 보철이 디자인 위치 그대로 합쳐진 파일입니다.',
        '에이전트가 케이스를 후보로 띄우면 체크 후 [선택분 생성]을 누릅니다. 합본이 없으면 "합본 필요"로 멈춥니다.',
    ] },
];
const GUIDE_3SHAPE = [
    { title: '처음 한 번 (Control Panel)', steps: [
        'Site settings → Manufacturing processes에서 커스텀 어버트먼트 공정의 "Apply enhanced transformation"을 끕니다. 켜져 있으면 STL이 제작용 자세로 눕혀져 악궁 위치를 잃습니다.',
        '같은 공정의 CAM 출력 DLL(CADOutputRAWSTL)에서 "Output Margin Line"을 켜 두면 마진선(.pts)이 함께 나옵니다. 없어도 지그는 만들어지지만(자동 검출) 있으면 그 값을 우선 씁니다.',
        '덴클 에이전트에서 [지그] → "+ 3Shape 폴더 연결"로 C:\\3Shape\\ManufacturingDir\\Visualization output 폴더를 연결합니다.',
    ] },
    { title: '디자인할 때마다', steps: [
        '커스텀 어버트먼트 디자인을 끝내고 저장합니다.',
        'Dental Manager에서 주문을 우클릭 → Advanced → Export Visualisation output 을 실행합니다. 주문 ID 이름의 폴더가 Visualization output 아래에 생깁니다.',
        '에이전트가 그 폴더를 후보로 띄우면 체크 후 [선택분 생성]을 누릅니다. 주문 폴더(C:\\3shape\\<주문ID>)나 Ctrl+E 내보내기는 암호화라 쓸 수 없습니다.',
    ] },
];

const styles = {
    dropNote: { marginTop: 4, fontSize: 11.5, fontWeight: 600, color: '#b45309', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '3px 7px', maxWidth: 260, lineHeight: 1.35 },
    guideRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '0 20px', marginTop: -4 },
    guideLabel: { fontSize: 12, color: '#64748b' },
    guideBtn: { fontSize: 12, fontWeight: 600, color: '#334155', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 12px', cursor: 'pointer' },
    modelSoonBadge: { marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#64748b', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 10px' },   // 3D 모델 준비 중 (2026-10-30)
    guideBody: { padding: '8px 16px 16px', maxHeight: '70vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 },
    guideSec: { display: 'flex', flexDirection: 'column', gap: 4 },
    guideSecTitle: { fontSize: 13, fontWeight: 700, color: '#1e293b' },
    guideList: { margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 },
    guideItem: { fontSize: 13, color: '#334155', lineHeight: 1.5 },
    body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
    topRow: { display: 'flex', flexWrap: 'wrap', gap: 14 },
    // 미터
    meterCard: {
        flex: '2 1 380px', minWidth: 300, padding: 16, backgroundColor: '#fff',
        border: '1px solid #e2e8f0', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8,
    },
    meterHead: { display: 'flex', alignItems: 'center', gap: 8 },
    meterTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b' },
    meterMain: { marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: '#6366f1' },
    meterSub: { fontSize: 12, color: '#334155', display: 'flex', gap: 4, flexWrap: 'wrap' },
    meterDim: { color: '#94a3b8' },
    // 툴바
    pcSection: { display: 'flex', flexDirection: 'column', gap: 6 },   // PC에서 찾은 케이스 구역 (2026-10-30)
    pcTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b' },
    pcHint: { fontSize: 12.5, color: '#64748b' },
    toolBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    searchInput: {
        flex: '1 1 220px', minWidth: 0, maxWidth: 360, padding: '8px 12px',
        border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 13, color: '#0f172a', outline: 'none',
    },
    ghostBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
        border: '1px solid #cbd5e1', borderRadius: 8, backgroundColor: '#fff',
        color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    // 테이블
    empty: {
        padding: '48px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14,
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    },
    tableWrap: { overflowX: 'auto', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' },
    th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    thCenter: { textAlign: 'center', padding: '10px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    trFail: { borderBottom: '1px solid #f1f5f9', backgroundColor: '#fef2f2' },
    td: { padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle' },
    tdCenter: { padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle', textAlign: 'center' },
    tdEllip: {
        padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle',
        maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
    thumb: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 8, backgroundColor: '#f0fdfa', border: '1px solid #99f6e4',
    },
    // 미리보기 칸 — 3D·2D 같은 크기(64×48), 사진이 알아볼 만한 크기 (2026-10-23 대표)
    thumbBtn: { display: 'inline-flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 6 },
    thumbImg: { width: 96, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', display: 'block' },
    photoSlot: {
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        width: 64, height: 48, borderRadius: 8, backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1',
        cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 600, color: '#64748b',
    },
    // 2D 사진 크게 보기 (자리만 — 사진 연결 전)
    photoOverlay: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    },
    photoModal: {
        width: '100%', maxWidth: 640, backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
    },
    photoModalHead: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontSize: 15, fontWeight: 700, color: '#1e293b',
    },
    photoClose: { border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', color: '#64748b' },
    photoBig: {
        aspectRatio: '4 / 3', margin: 16, borderRadius: 10, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        color: '#94a3b8', fontSize: 14,
    },
    actWrap: { display: 'inline-flex', alignItems: 'center', gap: 6 },
    failWrap: { display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 },   // 실패 사유 + 안내 줄 세로 (2026-10-24)
    rowBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
        border: '1px solid #cbd5e1', borderRadius: 7, backgroundColor: '#fff',
        color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    },
    rowBtnOutline: {
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
        border: '1px solid #c7d2fe', borderRadius: 7, backgroundColor: '#eef2ff',
        color: '#4338ca', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        whiteSpace: 'nowrap', flexShrink: 0,   // '다시 시도'가 두 줄로 쪼개지지 않게
    },
    remakeSel: { fontSize: 12, padding: '3px 4px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' },
    remakeBtn: { fontSize: 12, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#f8fafc', cursor: 'pointer' },
    // [지그 받기]·[모델 받기] 같은 크기·같은 모양 (대표 2026-10-28) — 글자 수가 달라도 폭을 맞춘다
    dlBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '5px 10px', minWidth: 96, height: 28, boxSizing: 'border-box',
        border: '1px solid #c7d2fe', borderRadius: 7, backgroundColor: '#eef2ff',
        color: '#4338ca', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    },
    gotMark: { fontSize: 12, color: '#0f766e', fontWeight: 600, whiteSpace: 'nowrap' },
    muted: { fontSize: 12, color: '#94a3b8' },
    failText: {
        display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', fontSize: 12, color: '#dc2626', fontWeight: 600, verticalAlign: 'bottom',
    },
    // 실패 사유 아래 '뭘 하면 되는지' 한 줄 (2026-10-24)
    hintText: { display: 'block', fontSize: 11, color: '#9a3412', lineHeight: 1.4, maxWidth: 190, whiteSpace: 'normal' },
    soonBadge: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px',
        backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: 999, fontSize: 11, fontWeight: 700,
    },
    footNote: { fontSize: 12, color: '#94a3b8', margin: '4px 2px 0', lineHeight: 1.6 },
};

export default Jig;
